/**
 * Utenti e assegnazioni — sezione riservata al Super Admin.
 *
 * L'autorizzazione ha due dimensioni: il **ruolo** (super admin o redattore) e
 * il **perimetro** (ente piu' insieme dei nodi assegnati). Questa pagina
 * governa la seconda: un redattore vede e modifica solo i nodi che gli sono
 * stati assegnati, e il vincolo e' verificato dall'API a ogni operazione, non
 * dedotto dall'interfaccia.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { assegnaNodi, elencaEnti, elencaUtenti, ErroreApi, invitaUtente } from "../api/client";
import type { Ente, IdNodo, Ruolo, Utente } from "../api/types";
import { CampoLibero, CampoScelta } from "../components/campi";
import { Modale } from "../components/Modale";
import {
  Avviso,
  Bottone,
  Chip,
  ChipNodo,
  Scheda,
  Scheletro,
  StatoVuoto,
} from "../components/primitive";
import { useAutenticazione } from "../auth/contesto";
import { useAlberatura } from "../dati/contesto-alberatura";
import { catenePerId, partiPercorso, percorsoAntenati, SEPARATORE_PERCORSO } from "../lib/alberatura";
import { nodiFoglia } from "../lib/scadenze";
import { useRisorsa } from "../lib/useRisorsa";
import { validaEmail, validaNomeUtente } from "../lib/formats";
import { IntestazionePagina } from "../layout/Guscio";

const ETICHETTA_RUOLO: Record<Ruolo, string> = {
  super_admin: "Super Admin",
  redattore: "Redattore",
};

/** Lunghezza minima imposta dall'API su `UtenteCreazione.password`. */
const LUNGHEZZA_MINIMA_PASSWORD = 10;

/**
 * Genera una password iniziale robusta.
 *
 * L'API non manda inviti per posta elettronica: l'utenza nasce con una password
 * e qualcuno deve consegnarla. Generarla qui evita che l'operatore scelga la
 * solita parola d'ordine di reparto; mostrarla in chiaro **una volta sola**, al
 * momento della creazione, e' l'unico modo per poterla trasmettere, visto che
 * `UtenteRisposta` non la restituisce mai.
 *
 * Alfabeto senza caratteri ambigui (`0`/`O`, `1`/`l`/`I`): la password viene
 * riletta e ridigitata da una persona.
 */
function generaPassword(): string {
  const alfabeto = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789-_";
  const casuali = new Uint32Array(16);
  crypto.getRandomValues(casuali);
  return Array.from(casuali, (valore) => alfabeto[valore % alfabeto.length]).join("");
}

export function Utenti() {
  const { nodi } = useAlberatura();
  const { impersonifica, utente: proprioProfilo } = useAutenticazione();
  const naviga = useNavigate();
  const carica = useCallback((segnale: AbortSignal) => elencaUtenti(segnale), []);
  const { dato, inCaricamento, errore, ricarica } = useRisorsa(carica, []);

  const [inInvito, setInInvito] = useState(false);
  const [daAssegnare, setDaAssegnare] = useState<Utente | null>(null);
  const [daImpersonificare, setDaImpersonificare] = useState<Utente | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [erroreImpersonificazione, setErroreImpersonificazione] = useState<string | null>(
    null,
  );

  useEffect(() => {
    document.title = "Utenti · Amministrazione trasparente";
  }, []);

  const utenti = dato ?? [];
  const foglie = useMemo(() => nodiFoglia(nodi), [nodi]);

  // Percorso completo del nodo, dalla sezione al padre. Con tre livelli il solo
  // padre non basta a distinguere gli omonimi: «Provvedimenti» esiste sia sotto
  // «Enti controllati › Societa' partecipate» sia come sezione a se'.
  const catene = useMemo(() => catenePerId(nodi), [nodi]);

  /**
   * Voci del selettore: la stessa forma per entrambe le modali.
   *
   * Sono due modali ma un solo perimetro: offrire il contesto solo in una delle
   * due lasciava chi crea l'utenza a scegliere fra caselle identiche — fra i
   * 121 obblighi ci sono due coppie di denominazioni perfettamente uguali.
   */
  const vociNodo = useMemo(
    () =>
      foglie.map((nodo) => ({
        id: nodo.id_nodo,
        etichetta: nodo.denominazione,
        padre: percorsoAntenati(catene, nodo.id_nodo),
      })),
    [foglie, catene],
  );

  const avviaImpersonificazione = async (utente: Utente) => {
    setErroreImpersonificazione(null);
    setInCorso(true);
    try {
      await impersonifica(utente.id);
      setDaImpersonificare(null);
      // Si atterra sul cruscotto: e' la pagina da cui si vede subito con quali
      // occhi si sta guardando, e la fascia in testa lo dichiara.
      naviga("/");
    } catch (causa) {
      setErroreImpersonificazione(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Non e' stato possibile avviare l'impersonificazione.",
      );
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Amministrazione"
        titolo="Utenti e assegnazioni"
        sottotitolo="Creazione delle utenze e definizione del perimetro di ciascuna. Il super admin puo' operare su tutti i nodi; il redattore solo su quelli assegnati nel proprio ente."
        azioni={
          <Bottone variante="primario" onClick={() => setInInvito(true)}>
            Crea un'utenza
          </Bottone>
        }
      />

      {errore ? (
        <Avviso tono="critico" titolo="Elenco non disponibile">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}

      <Scheda titolo={`Utenti (${utenti.length})`} fitto>
        {inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={5} />
          </div>
        ) : utenti.length === 0 ? (
          <StatoVuoto
            titolo="Nessun utente"
            testo="Crei la prima utenza di redazione e ne definisca il perimetro di nodi."
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Nome utente</th>
                  <th scope="col">Ruolo</th>
                  <th scope="col">Stato</th>
                  <th scope="col">Nodi assegnati</th>
                  <th scope="col">
                    <span className="tabella__azioni">Azioni</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {utenti.map((utente) => (
                  <tr key={utente.id}>
                    <td className="tabella__principale">
                      {utente.nome_utente}
                      {/* L'email e' un recapito facoltativo: sotto il nome, non in
                          una colonna propria che toglierebbe spazio al perimetro. */}
                      {utente.email ? (
                        <>
                          <br />
                          <span className="testo-fioco">{utente.email}</span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Chip tono={utente.ruolo === "super_admin" ? "critico" : "primario"}>
                        {ETICHETTA_RUOLO[utente.ruolo]}
                      </Chip>
                    </td>
                    <td>
                      <Chip tono={utente.attivo ? "positivo" : "neutro"}>
                        {utente.attivo ? "Attivo" : "Sospeso"}
                      </Chip>
                    </td>
                    <td className="testo-tenue">
                      {utente.ruolo === "super_admin" ? (
                        <span className="campo__aiuto">Tutti i nodi, per ruolo.</span>
                      ) : (utente.nodi_assegnati ?? []).length === 0 ? (
                        <span className="campo__errore">Nessun nodo assegnato</span>
                      ) : (
                        <div className="riga">
                          {/* Il percorso completo, non la sola denominazione:
                              e' il perimetro di un'utenza, e due caselle
                              tipograficamente identiche non sono un perimetro
                              verificabile. */}
                          {(utente.nodi_assegnati ?? []).slice(0, 3).map((idNodo) => (
                            <ChipNodo
                              key={idNodo}
                              {...partiPercorso(catene, idNodo)}
                              separatore={SEPARATORE_PERCORSO}
                            />
                          ))}
                          {(utente.nodi_assegnati ?? []).length > 3 ? (
                            <span className="campo__aiuto">
                              e altri {(utente.nodi_assegnati ?? []).length - 3}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="tabella__azioni">
                      <Bottone
                        variante="contorno"
                        dimensione="piccolo"
                        onClick={() => setDaAssegnare(utente)}
                        disabled={utente.ruolo === "super_admin"}
                      >
                        Assegna nodi
                      </Bottone>{" "}
                      {/* Le stesse condizioni che l'API impone: su un altro
                          Super Admin, su se stessi e su un'utenza sospesa la
                          risposta sarebbe un conflitto, e non si offre un
                          comando che finisce cosi'. */}
                      <Bottone
                        variante="fantasma"
                        dimensione="piccolo"
                        onClick={() => setDaImpersonificare(utente)}
                        disabled={
                          utente.ruolo === "super_admin" ||
                          !utente.attivo ||
                          utente.id === proprioProfilo?.id
                        }
                        aria-label={`Opera nei panni di ${utente.nome_utente}`}
                      >
                        Impersonifica
                      </Bottone>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>

      {/* Le modali si montano e si smontano: lo stato del modulo nasce e muore
          con loro, senza bisogno di azzerarlo a mano. */}
      {inInvito ? (
        <ModaleInvito
          nodi={vociNodo}
          onChiudi={() => setInInvito(false)}
          onInvitato={() => {
            setInInvito(false);
            ricarica();
          }}
        />
      ) : null}

      {daImpersonificare ? (
        <Modale
          titolo={`Operare nei panni di ${daImpersonificare.nome_utente}?`}
          aperta
          onChiudi={() => setDaImpersonificare(null)}
          pedice={
            <>
              <Bottone variante="fantasma" onClick={() => setDaImpersonificare(null)}>
                Annulla
              </Bottone>
              <Bottone
                variante="primario"
                inCorso={inCorso}
                onClick={() => void avviaImpersonificazione(daImpersonificare)}
              >
                Impersonifica per un'ora
              </Bottone>
            </>
          }
        >
          <div className="pila">
            {erroreImpersonificazione ? (
              <Avviso tono="critico" titolo="Impersonificazione non avviata">
                {erroreImpersonificazione}
              </Avviso>
            ) : null}

            <p>
              Per <strong>un'ora</strong> vedrai il back-office con i poteri di questa utenza: il
              suo perimetro, le sue allerte, i comandi che le sono offerti. La configurazione —
              alberatura, utenti, enti, schemi — resta chiusa, perche' il token porta i poteri di
              lei e non i tuoi.
            </p>

            {/* La cosa scomoda, detta prima e non dopo. */}
            <Avviso tono="attenzione" titolo="Cio' che scrivi non portera' la tua firma">
              Ogni contenuto caricato, pubblicato o deciso durante l'impersonificazione risultera'
              di <strong>{daImpersonificare.nome_utente}</strong>, senza alcuna traccia della tua
              utenza. Vale anche per il giornale delle valutazioni: un verdetto scritto da te in
              questi panni sara' indistinguibile da uno suo. Se devi agire, conviene farlo con la
              tua utenza.
            </Avviso>

            <p className="campo__aiuto">
              La tua sessione non viene chiusa: resta da parte, e si torna con «Torna alla tua
              utenza» nella fascia in testa alla pagina. Alla scadenza dell'ora il token smette di
              valere.
            </p>
          </div>
        </Modale>
      ) : null}

      {daAssegnare ? (
        <ModaleAssegnazioni
          key={daAssegnare.id}
          utente={daAssegnare}
          nodi={vociNodo}
          onChiudi={() => setDaAssegnare(null)}
          onSalvato={() => {
            setDaAssegnare(null);
            ricarica();
          }}
        />
      ) : null}
    </div>
  );
}

interface VoceNodo {
  id: IdNodo;
  etichetta: string;
  padre?: string | null;
}

/**
 * Creazione di un nuovo utente, con ente e perimetro iniziale.
 *
 * Tre vincoli vengono dall'API e non sono negoziabili in interfaccia:
 * la `password` e' obbligatoria (almeno 10 caratteri), il redattore deve avere
 * un `ente_id`, e il Super Admin deve averlo nullo e nessun nodo assegnato.
 */
function ModaleInvito({
  nodi,
  onChiudi,
  onInvitato,
}: {
  nodi: VoceNodo[];
  onChiudi: () => void;
  onInvitato: () => void;
}) {
  const caricaEnti = useCallback((segnale: AbortSignal) => elencaEnti(segnale), []);
  const { dato: enti, inCaricamento: entiInCaricamento } = useRisorsa(caricaEnti, []);

  const [nomeUtente, setNomeUtente] = useState("");
  const [email, setEmail] = useState("");
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [ruolo, setRuolo] = useState<Ruolo>("redattore");
  const [enteScelto, setEnteScelto] = useState("");
  // Generata una sola volta al montaggio della modale: rigenerarla a ogni render
  // mostrerebbe all'operatore una password diversa da quella che verra' inviata.
  const [password, setPassword] = useState(generaPassword);
  const [selezionati, setSelezionati] = useState<IdNodo[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [creata, setCreata] = useState<{ nomeUtente: string; password: string } | null>(null);

  const elenco: Ente[] = enti ?? [];
  const richiedeEnte = ruolo === "redattore";

  const erroreNomeUtente = nomeUtente.trim() ? validaNomeUtente(nomeUtente) : null;
  const erroreEmail = email.trim() ? validaEmail(email) : null;
  const errorePassword =
    password.length > 0 && password.length < LUNGHEZZA_MINIMA_PASSWORD
      ? `La password deve avere almeno ${LUNGHEZZA_MINIMA_PASSWORD} caratteri.`
      : null;
  const erroreEnte =
    richiedeEnte && !enteScelto && !entiInCaricamento
      ? "Un redattore deve appartenere a un ente: l'ente e' il primo livello del suo perimetro."
      : null;

  const pronto =
    Boolean(nomeUtente.trim()) &&
    !erroreNomeUtente &&
    !erroreEmail &&
    password.length >= LUNGHEZZA_MINIMA_PASSWORD &&
    (!richiedeEnte || Boolean(enteScelto));

  const invia = async () => {
    setErrore(null);
    setInCorso(true);
    try {
      await invitaUtente({
        nome_utente: nomeUtente.trim().toLowerCase(),
        email: email.trim() || null,
        password,
        nome_completo: nomeCompleto.trim() || null,
        ruolo,
        // Nullo per il Super Admin, che e' cross-ente: valorizzarlo lo legherebbe
        // a un ente e il validatore dell'API respingerebbe la richiesta.
        ente_id: richiedeEnte ? Number(enteScelto) : null,
        nodi_assegnati: richiedeEnte ? selezionati : [],
      });
      // La password non e' piu' recuperabile da nessuna parte: si mostra qui,
      // una volta, prima di chiudere.
      setCreata({ nomeUtente: nomeUtente.trim().toLowerCase(), password });
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi ? causa.messaggioEsteso : "Creazione non riuscita.",
      );
    } finally {
      setInCorso(false);
    }
  };

  if (creata) {
    return (
      <Modale
        titolo="Utenza creata"
        aperta
        onChiudi={onInvitato}
        pedice={
          <Bottone variante="primario" onClick={onInvitato}>
            Ho annotato le credenziali
          </Bottone>
        }
      >
        <div className="pila">
          <Avviso tono="attenzione" titolo="La password non sara' piu' visibile">
            Va consegnata a {creata.nomeUtente} per un canale sicuro e cambiata al primo accesso. Se
            viene persa, l'unico rimedio e' impostarne una nuova da questa pagina.
          </Avviso>
          <div>
            <p className="anteprima__etichetta">Nome utente</p>
            <p className="dato">{creata.nomeUtente}</p>
            <p className="anteprima__etichetta" style={{ marginTop: "0.6rem" }}>
              Password iniziale
            </p>
            <p className="dato">{creata.password}</p>
          </div>
        </div>
      </Modale>
    );
  }

  return (
    <Modale
      titolo="Crea un'utenza"
      aperta
      onChiudi={onChiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={onChiudi}>
            Annulla
          </Bottone>
          <Bottone variante="primario" onClick={invia} disabled={!pronto} inCorso={inCorso}>
            Crea l'utenza
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {errore ? (
          <Avviso tono="critico" titolo="Creazione non riuscita">
            {errore}
          </Avviso>
        ) : null}

        <CampoLibero
          etichetta="Nome utente"
          valore={nomeUtente}
          onCambia={setNomeUtente}
          obbligatorio
          errore={erroreNomeUtente}
          segnaposto="es. mario.rossi"
          aiuto="E' cio' con cui si accede: lettere minuscole, cifre, «.», «_» e «-»."
        />

        <CampoLibero
          etichetta="Posta elettronica"
          tipo="email"
          valore={email}
          onCambia={setEmail}
          errore={erroreEmail}
          segnaposto="nome.cognome@comune.esempio.to.it"
          aiuto="Facoltativa: e' un recapito, non serve per accedere."
        />

        <CampoLibero
          etichetta="Nome e cognome"
          valore={nomeCompleto}
          onCambia={setNomeCompleto}
          segnaposto="Maria Rossi"
          aiuto="Facoltativo."
        />

        <CampoScelta
          etichetta="Ruolo"
          valore={ruolo}
          onCambia={(valore) => setRuolo(valore as Ruolo)}
          opzioni={[
            { valore: "redattore", etichetta: "Redattore — pubblica nei nodi assegnati" },
            { valore: "super_admin", etichetta: "Super Admin — configura il sistema" },
          ]}
          vuoto={null}
          aiuto="Il super admin e' l'unico a poter definire l'alberatura e la configurazione."
        />

        {richiedeEnte ? (
          <CampoScelta
            etichetta="Ente"
            valore={enteScelto}
            onCambia={setEnteScelto}
            obbligatorio
            errore={erroreEnte}
            disabilitato={entiInCaricamento}
            opzioni={elenco.map((ente) => ({
              valore: String(ente.id),
              etichetta: `${ente.denominazione} — ${ente.dominio}`,
            }))}
            vuoto={entiInCaricamento ? "Caricamento degli enti…" : "— seleziona —"}
            aiuto="Il redattore opera solo dentro il proprio ente: e' il primo livello del perimetro."
          />
        ) : null}

        <CampoLibero
          etichetta="Password iniziale"
          valore={password}
          onCambia={setPassword}
          obbligatorio
          errore={errorePassword}
          autoComplete="off"
          aiuto={`Generata automaticamente e mostrata una sola volta, subito dopo la creazione. Almeno ${LUNGHEZZA_MINIMA_PASSWORD} caratteri.`}
        />
        <Bottone variante="contorno" dimensione="piccolo" onClick={() => setPassword(generaPassword())}>
          Genera un'altra password
        </Bottone>

        {richiedeEnte ? (
          <SelettoreNodi
            nodi={nodi}
            selezionati={selezionati}
            onCambia={setSelezionati}
            aiuto="Perimetro iniziale. Puo' essere modificato in qualsiasi momento."
          />
        ) : (
          <Avviso tono="attenzione" titolo="Ruolo con pieni poteri">
            Il Super Admin puo' modificare l'alberatura, gli enti, gli utenti e gli schemi ANAC di
            tutti gli enti configurati. Non appartiene ad alcun ente e non si assegna a singoli
            nodi: il suo perimetro deriva dal ruolo.
          </Avviso>
        )}
      </div>
    </Modale>
  );
}

/** Modifica del perimetro di un redattore. */
function ModaleAssegnazioni({
  utente,
  nodi,
  onChiudi,
  onSalvato,
}: {
  utente: Utente;
  nodi: VoceNodo[];
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const [selezionati, setSelezionati] = useState<IdNodo[]>(utente.nodi_assegnati ?? []);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const salva = async () => {
    setErrore(null);
    setInCorso(true);
    try {
      // Il perimetro ha un endpoint proprio e **sostitutivo**:
      // `PUT /api/v1/utenti/{id}` rifiuta `nodi_assegnati` (extra="forbid").
      await assegnaNodi(utente.id, selezionati);
      onSalvato();
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi ? causa.messaggioEsteso : "Aggiornamento non riuscito.",
      );
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Modale
      titolo={`Nodi assegnati a ${utente.nome_utente}`}
      aperta
      onChiudi={onChiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={onChiudi}>
            Annulla
          </Bottone>
          <Bottone variante="primario" onClick={salva} inCorso={inCorso}>
            Salva il perimetro
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {errore ? (
          <Avviso tono="critico" titolo="Aggiornamento non riuscito">
            {errore}
          </Avviso>
        ) : null}
        <SelettoreNodi
          nodi={nodi}
          selezionati={selezionati}
          onCambia={setSelezionati}
          aiuto="Il redattore potra' creare e pubblicare contenuti solo nei nodi selezionati."
        />
      </div>
    </Modale>
  );
}

/**
 * Elenco di caselle con ricerca, per scegliere i nodi del perimetro.
 *
 * SELEZIONE IN BLOCCO
 * -------------------
 * I 121 obblighi, spuntati uno per uno, sono 121 clic: per un Comune piccolo,
 * dove chi redige e' una persona sola e il perimetro e' «tutto», era il caso
 * peggiore proprio perche' e' il piu' comune.
 *
 * I comandi agiscono su **cio' che si vede**, non sull'elenco intero, e lo
 * dicono nell'etichetta: con un filtro attivo «Seleziona tutti» che spuntasse
 * anche i nodi fuori filtro darebbe a un'utenza un perimetro che l'operatore
 * non ha mai avuto sotto gli occhi. Il perimetro e' un fatto di autorizzazione:
 * si concede quel che si e' guardato.
 */
function SelettoreNodi({
  nodi,
  selezionati,
  onCambia,
  aiuto,
}: {
  nodi: VoceNodo[];
  selezionati: IdNodo[];
  onCambia: (successivi: IdNodo[]) => void;
  aiuto?: string;
}) {
  const [filtro, setFiltro] = useState("");

  const filtrato = filtro.trim().length > 0;
  const visibili = nodi.filter((nodo) =>
    `${nodo.padre ?? ""} ${nodo.etichetta}`.toLowerCase().includes(filtro.trim().toLowerCase()),
  );

  const commuta = (idNodo: IdNodo) => {
    onCambia(
      selezionati.includes(idNodo)
        ? selezionati.filter((voce) => voce !== idNodo)
        : [...selezionati, idNodo],
    );
  };

  const scelti = new Set(selezionati);
  const daAggiungere = visibili.filter((nodo) => !scelti.has(nodo.id));
  const daTogliere = visibili.filter((nodo) => scelti.has(nodo.id));

  // L'ordine dei selezionati non conta — l'API sostituisce l'insieme — ma
  // accodare senza duplicare si', perche' `commuta` confronta per valore.
  const selezionaVisibili = () =>
    onCambia([...selezionati, ...daAggiungere.map((nodo) => nodo.id)]);

  const deselezionaVisibili = () => {
    const visti = new Set(visibili.map((nodo) => nodo.id));
    onCambia(selezionati.filter((idNodo) => !visti.has(idNodo)));
  };

  // Per copertura e non per conteggio: un perimetro salvato puo' contenere un
  // identificativo che fra le foglie non c'e' piu' — un nodo rimosso, o mai
  // stato foglia — e allora i due numeri coincidono senza che l'insieme sia
  // completo.
  const tuttiScelti = nodi.length > 0 && nodi.every((nodo) => scelti.has(nodo.id));

  return (
    <div className="campo">
      <span className="campo__etichetta">
        Nodi assegnati ({selezionati.length} su {nodi.length})
      </span>
      {aiuto ? <p className="campo__aiuto">{aiuto}</p> : null}
      <input
        type="search"
        className="campo__controllo"
        placeholder="Filtra per denominazione…"
        value={filtro}
        onChange={(evento) => setFiltro(evento.target.value)}
        aria-label="Filtra i nodi"
      />

      <div className="riga" style={{ margin: "0.5rem 0" }}>
        <Bottone
          variante="contorno"
          dimensione="piccolo"
          onClick={selezionaVisibili}
          disabled={daAggiungere.length === 0}
        >
          {filtrato
            ? `Seleziona i ${daAggiungere.length} filtrati`
            : `Seleziona tutti (${nodi.length})`}
        </Bottone>
        <Bottone
          variante="fantasma"
          dimensione="piccolo"
          onClick={deselezionaVisibili}
          disabled={daTogliere.length === 0}
        >
          {filtrato ? `Deseleziona i ${daTogliere.length} filtrati` : "Azzera la selezione"}
        </Bottone>
      </div>

      {tuttiScelti ? (
        <Avviso tono="attenzione" titolo="Perimetro esteso a tutto l'ente">
          L'utenza potra' creare e pubblicare su ogni obbligo dell'ente. Resta un redattore: la
          configurazione — alberatura, utenti, enti, schemi ANAC — non le e' accessibile, e il
          vincolo e' verificato dall'API a ogni operazione.
        </Avviso>
      ) : null}

      <div className="elenco-caselle">
        {visibili.length === 0 ? (
          <p className="campo__aiuto" style={{ padding: "0.5rem" }}>
            Nessun nodo corrisponde al filtro.
          </p>
        ) : (
          visibili.map((nodo) => (
            <label key={nodo.id} className="casella">
              <input
                type="checkbox"
                checked={selezionati.includes(nodo.id)}
                onChange={() => commuta(nodo.id)}
              />
              <span>
                {nodo.padre ? <span className="testo-fioco">{nodo.padre} › </span> : null}
                {nodo.etichetta}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
